import { usePageTitle } from '@/hooks/usePageTitle';
import PortalNav from '@/components/PortalNav';
import PortalFooter from '@/components/PortalFooter';
import { portalPageStyles } from '@/styles/portalPageStyles';

const sectionStyle: React.CSSProperties = {
  marginBottom: '2rem',
};

const headingStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  marginBottom: '1rem',
  marginTop: 0,
};

const subHeadingStyle: React.CSSProperties = {
  fontSize: '1.15rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  marginBottom: '0.75rem',
  marginTop: '1.25rem',
};

const paragraphStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.7,
  marginBottom: '0.75rem',
};

const listStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.7,
  paddingLeft: '1.5rem',
  marginBottom: '0.75rem',
};

function PrivacyPolicyPage() {
  usePageTitle('Privacy Policy');

  return (
    <div style={portalPageStyles.pageContainer}>
      <PortalNav />

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2.5rem 2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
          Privacy Policy
        </h1>
        <p style={{ ...paragraphStyle, marginBottom: '2rem' }}>
          Last updated: February 12, 2026
        </p>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. Introduction</h2>
          <p style={paragraphStyle}>
            Checkademic ("we," "us," or "our") is a multi-tenant software-as-a-service (SaaS) platform
            that helps K-12 schools track student behavior and attendance. This Privacy Policy describes
            how we collect, use, and protect information when you use our services at checkademic.com and
            any school subdomains (collectively, the "Service").
          </p>
          <p style={paragraphStyle}>
            We are committed to protecting the privacy of students, parents, teachers, and school
            administrators who use our platform.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. Information We Collect</h2>

          <h3 style={subHeadingStyle}>Account Information</h3>
          <ul style={listStyle}>
            <li>Name, email address, and role (teacher, administrator, parent, support staff)</li>
            <li>Authentication credentials (passwords are hashed and never stored in plain text)</li>
            <li>School affiliation and subdomain</li>
          </ul>

          <h3 style={subHeadingStyle}>Student Data</h3>
          <ul style={listStyle}>
            <li>Student names, grade levels, and school enrollment information</li>
            <li>Behavior records (incidents and recognitions) logged by authorized school staff</li>
            <li>Attendance records</li>
            <li>Student photographs (when uploaded by school administrators)</li>
          </ul>

          <h3 style={subHeadingStyle}>Parent Data</h3>
          <ul style={listStyle}>
            <li>Parent/guardian names, email addresses, and phone numbers</li>
            <li>Notification preferences (digest frequency, opted-in categories, SMS opt-in status)</li>
            <li>Parent-student relationships as defined by the school</li>
          </ul>

          <h3 style={subHeadingStyle}>Usage Data</h3>
          <ul style={listStyle}>
            <li>Login timestamps and session information</li>
            <li>Audit logs of actions taken within the platform (for security and accountability)</li>
            <li>Email delivery logs (send status, bounce/complaint tracking)</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. How We Use Information</h2>
          <p style={paragraphStyle}>We use the information we collect to:</p>
          <ul style={listStyle}>
            <li>Provide and operate the behavior tracking and attendance service</li>
            <li>Send transactional emails (invitations, password resets, behavior digest reports)</li>
            <li>Send SMS text message notifications (behavior digest summaries to opted-in parents)</li>
            <li>Generate reports for school administrators</li>
            <li>Maintain audit trails for accountability and security</li>
            <li>Monitor and improve service reliability and performance</li>
            <li>Respond to support requests</li>
          </ul>
          <p style={paragraphStyle}>
            We do <strong>not</strong> use student data for advertising, marketing, or profiling purposes.
            We do <strong>not</strong> sell or rent personal information to third parties.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Data Isolation and Security</h2>
          <p style={paragraphStyle}>
            Checkademic uses a <strong>database-per-tenant architecture</strong>. Each school's data is
            stored in a completely separate database, ensuring that one school's data is never accessible
            to another school. This provides strong data isolation by design.
          </p>
          <p style={paragraphStyle}>Additional security measures include:</p>
          <ul style={listStyle}>
            <li>All data transmitted over HTTPS with TLS encryption</li>
            <li>Passwords hashed using industry-standard algorithms (bcrypt)</li>
            <li>JWT-based authentication with secure httpOnly cookie storage</li>
            <li>Automatic token rotation for session security</li>
            <li>Role-based access controls (teachers, administrators, parents, support staff)</li>
            <li>Complete audit logging of all data modifications</li>
            <li>Regular database backups with encryption</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Third-Party Services</h2>
          <p style={paragraphStyle}>We use the following third-party services to operate Checkademic:</p>
          <ul style={listStyle}>
            <li>
              <strong>RackNerd</strong> — VPS hosting infrastructure. Servers are located in the United States.
            </li>
            <li>
              <strong>Amazon Web Services (AWS)</strong> — Email delivery via Simple Email Service (SES).
            </li>
            <li>
              <strong>Twilio</strong> — SMS text message delivery for parent notifications.
            </li>
            <li>
              <strong>Cloudflare</strong> — DNS management, DDoS protection, and SSL/TLS termination.
            </li>
          </ul>
          <p style={paragraphStyle}>
            We do not share student data with any third parties for advertising, marketing, or data
            brokerage. Third-party services are used solely for infrastructure and delivery purposes.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Communications</h2>
          <p style={paragraphStyle}>Checkademic sends the following types of emails:</p>
          <ul style={listStyle}>
            <li><strong>Transactional emails:</strong> Account invitations, password resets, and support ticket notifications. These are required for service operation and cannot be unsubscribed from.</li>
            <li><strong>Behavior digest emails:</strong> Daily or weekly summaries of student behavior sent to opted-in parents. Parents can unsubscribe at any time via one-click unsubscribe or notification settings.</li>
          </ul>
          <p style={paragraphStyle}>
            We implement email authentication standards (SPF, DKIM, DMARC) and maintain a suppression
            list for bounced and complained email addresses.
          </p>
          <p style={paragraphStyle}>
            <strong>SMS text message notifications:</strong> Parents who opt in to SMS receive brief behavior
            and attendance digest summaries via text message. Parents may opt out at any time by
            replying STOP to any message or by disabling SMS in their notification settings. Message
            and data rates may apply. Message frequency varies: up to 1 message per day for daily
            digests or 1 per week for weekly digests.
            See our <a href="/sms-terms" style={{ color: 'var(--primary-color)' }}>SMS Terms</a> page
            for full details.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Student Data Protection</h2>
          <p style={paragraphStyle}>
            We recognize the sensitivity of student educational records. Checkademic is designed to
            support schools in maintaining compliance with the Family Educational Rights and Privacy Act
            (FERPA) and other applicable student privacy laws:
          </p>
          <ul style={listStyle}>
            <li>Student data is entered and managed exclusively by authorized school personnel</li>
            <li>Access to student records is restricted to authorized school staff and linked parents/guardians</li>
            <li>Schools retain ownership and control of all student data</li>
            <li>We act as a "school official" with a "legitimate educational interest" under FERPA, as defined by our agreements with schools</li>
            <li>We do not use student data for any purpose other than providing the contracted service</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Data Retention and Deletion</h2>
          <p style={paragraphStyle}>
            Schools control their own data retention. Student records, behavior logs, and attendance
            data are retained for as long as the school maintains an active account.
          </p>
          <ul style={listStyle}>
            <li>Schools may request complete deletion of their data at any time</li>
            <li>Soft-deleted files are permanently removed after 24 hours</li>
            <li>Expired authentication tokens are automatically cleaned up</li>
            <li>Upon account termination, all tenant data (including the isolated database) is permanently deleted</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Data Ownership</h2>
          <p style={paragraphStyle}>
            Schools own all data they enter into Checkademic. We do not claim ownership of any student
            data, behavior records, attendance records, or other school-generated content. Schools may
            export their data at any time through the platform's reporting tools or by contacting support.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Children's Privacy</h2>
          <p style={paragraphStyle}>
            Checkademic is not directed at children under 13. Students do not create accounts or
            interact with the platform directly. All student data is entered and managed by authorized
            school staff and accessed by linked parents/guardians. We do not knowingly collect personal
            information directly from children.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Changes to This Policy</h2>
          <p style={paragraphStyle}>
            We may update this Privacy Policy from time to time. We will notify school administrators
            of material changes via email. The "Last updated" date at the top of this page indicates
            when the policy was last revised.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>12. Contact Us</h2>
          <p style={paragraphStyle}>
            If you have questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <p style={paragraphStyle}>
            Email: <a href="mailto:contact@checkademic.com" style={{ color: 'var(--primary-color)' }}>contact@checkademic.com</a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <PortalFooter currentPage="privacy" />
    </div>
  );
}

export default PrivacyPolicyPage;
